import { 
  Document, 
  DocumentCategory, 
  DocumentV2, 
  DocumentCategoryV2, 
  DocumentPackage,
  CustomCategory,
  AssignmentStatus 
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.spedflow.io';

const getAuthHeaders = () => {
  const token = localStorage.getItem('idToken');
  if (!token) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

// ========================================
// V1 Types (Legacy)
// ========================================

export interface UploadRequest {
  caseId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  category: DocumentCategory;
  description?: string;
}

export interface UploadResponse {
  document: Document;
  uploadUrl: string;
  expiresIn: number;
}

export interface DownloadResponse {
  downloadUrl: string;
  fileName: string;
  expiresIn: number;
}

// ========================================
// V2 Types
// ========================================

export interface BatchUploadFile {
  fileName: string;
  fileType: string;
  fileSize: number;
}

export interface BatchUploadRequest {
  files: BatchUploadFile[];
  caseId?: string;
  assembleAsOne?: boolean;
}

export interface BatchUploadDocument {
  documentId: string;
  uploadUrl: string;
  s3Key: string;
}

export interface BatchUploadResponse {
  documents: BatchUploadDocument[];
  assemblyId?: string;
}

export interface ConfirmUploadRequest {
  documentIds: string[];
}

export interface ConfirmUploadResponse {
  confirmed: string[];
  failed: Array<{ documentId: string; error: string }>;
}

export interface AssignDocumentRequest {
  documentId: string;
  caseId: string;
  advocateId?: string;
}

export interface AssignDocumentResponse {
  documentId: string;
  caseId: string;
  status: 'assigned' | 'processing';
}

export interface PendingDocument {
  documentId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
  source: 'upload' | 'email';
  assignmentStatus: AssignmentStatus;
  suggestedCaseId?: string;
  suggestedCaseConfidence?: number;
}

export interface UpdateDocumentRequest {
  advocateCategory?: DocumentCategoryV2 | string;
  advocateMetadata?: Record<string, unknown>;
  advocateNotes?: string;
}

// Package types
export interface CreatePackageRequest {
  caseId: string;
  name: string;
  notes?: string;
  documentIds?: string[];
}

export interface UpdatePackageRequest {
  name?: string;
  notes?: string;
}

// Version types
export interface LinkVersionRequest {
  linkedDocumentId: string;
  newerDocumentId: string;
}

export interface ConfirmVersionRequest {
  confirmed: boolean;
}

// ========================================
// Documents API
// ========================================

export const documentsApi = {
  // V1 Legacy methods
  async initiateUpload(request: UploadRequest): Promise<UploadResponse> {
    const res = await fetch(`${API_URL}/documents`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to initiate upload');
    return data.data;
  },

  async confirmUploadLegacy(documentId: string): Promise<Document> {
    const res = await fetch(`${API_URL}/documents/${documentId}/confirm`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to confirm upload');
    return data.data.document;
  },

  // V2 Batch upload methods
  async uploadBatch(request: BatchUploadRequest): Promise<BatchUploadResponse> {
    const res = await fetch(`${API_URL}/documents`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to initiate batch upload');
    return data.data;
  },

  async confirmUpload(documentIds: string[]): Promise<ConfirmUploadResponse> {
    const res = await fetch(`${API_URL}/documents/confirm`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ documentIds }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to confirm upload');
    return data.data;
  },

  async list(caseId?: string): Promise<{ documents: DocumentV2[]; count: number }> {
    const url = caseId ? `${API_URL}/documents?caseId=${caseId}` : `${API_URL}/documents`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to list documents');
    return data.data;
  },

  async get(documentId: string): Promise<DocumentV2> {
    const res = await fetch(`${API_URL}/documents/${documentId}`, {
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to get document');
    return data.data;
  },

  async update(documentId: string, request: UpdateDocumentRequest): Promise<DocumentV2> {
    const res = await fetch(`${API_URL}/documents/${documentId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update document');
    return data.data;
  },

  async getDownloadUrl(documentId: string): Promise<DownloadResponse> {
    const res = await fetch(`${API_URL}/documents/${documentId}/download`, {
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to get download URL');
    return data.data;
  },

  async delete(documentId: string): Promise<void> {
    const res = await fetch(`${API_URL}/documents/${documentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to delete document');
  },

  // Assignment methods
  async assignDocument(request: AssignDocumentRequest): Promise<AssignDocumentResponse> {
    const res = await fetch(`${API_URL}/documents/${request.documentId}/assign`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ caseId: request.caseId, advocateId: request.advocateId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to assign document');
    return data.data;
  },

  async getPendingDocuments(): Promise<{ documents: PendingDocument[]; count: number }> {
    const res = await fetch(`${API_URL}/documents/pending`, {
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to get pending documents');
    return data.data;
  },

  // Reprocessing
  async reprocessDocument(documentId: string): Promise<DocumentV2> {
    const res = await fetch(`${API_URL}/documents/${documentId}/reprocess`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to reprocess document');
    return data.data;
  },

  // Assembly methods
  async assembleDocuments(documentIds: string[]): Promise<DocumentV2> {
    const res = await fetch(`${API_URL}/documents/assemble`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ documentIds }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to assemble documents');
    return data.data;
  },

  async disassembleDocument(documentId: string): Promise<void> {
    const res = await fetch(`${API_URL}/documents/${documentId}/disassemble`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to disassemble document');
  },

  // Version methods
  async linkVersion(documentId: string, request: LinkVersionRequest): Promise<DocumentV2> {
    const res = await fetch(`${API_URL}/documents/${documentId}/link-version`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to link version');
    return data.data;
  },

  async unlinkVersion(documentId: string): Promise<void> {
    const res = await fetch(`${API_URL}/documents/${documentId}/link-version`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to unlink version');
  },

  async confirmVersion(documentId: string, confirmed: boolean): Promise<DocumentV2> {
    const res = await fetch(`${API_URL}/documents/${documentId}/confirm-version`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ confirmed }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to confirm version');
    return data.data;
  },

  async getVersionHistory(documentId: string): Promise<{ documents: DocumentV2[] }> {
    const res = await fetch(`${API_URL}/documents/${documentId}/versions`, {
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to get version history');
    return data.data;
  },

  // Helper to upload file directly to S3
  async uploadToS3(uploadUrl: string, file: File, onProgress?: (progress: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error('Failed to upload file to storage'));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Failed to upload file to storage'));
      });
      
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },

  // Combined batch upload flow with progress tracking
  async uploadDocuments(
    files: File[],
    options: {
      caseId?: string;
      assembleAsOne?: boolean;
      onProgress?: (fileIndex: number, progress: number) => void;
      onFileComplete?: (fileIndex: number, documentId: string) => void;
    } = {}
  ): Promise<{ documentIds: string[]; assemblyId?: string }> {
    const { caseId, assembleAsOne, onProgress, onFileComplete } = options;

    // 1. Initiate batch upload
    const uploadRequest: BatchUploadRequest = {
      files: files.map(f => ({
        fileName: f.name,
        fileType: f.type,
        fileSize: f.size,
      })),
      caseId,
      assembleAsOne,
    };

    const { documents, assemblyId } = await this.uploadBatch(uploadRequest);

    // 2. Upload each file to S3
    const uploadPromises = documents.map(async (doc, index) => {
      await this.uploadToS3(doc.uploadUrl, files[index], (progress) => {
        onProgress?.(index, progress);
      });
      onFileComplete?.(index, doc.documentId);
      return doc.documentId;
    });

    const documentIds = await Promise.all(uploadPromises);

    // 3. Confirm all uploads
    await this.confirmUpload(documentIds);

    return { documentIds, assemblyId };
  },

  // V1 Legacy combined upload flow
  async upload(caseId: string, file: File, category: DocumentCategory, description?: string): Promise<Document> {
    const { document, uploadUrl } = await this.initiateUpload({
      caseId,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      category,
      description,
    });

    await this.uploadToS3(uploadUrl, file);
    return await this.confirmUploadLegacy(document.documentId);
  },
};

// ========================================
// Packages API
// ========================================

export const packagesApi = {
  async create(request: CreatePackageRequest): Promise<DocumentPackage> {
    const res = await fetch(`${API_URL}/packages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create package');
    return data.data;
  },

  async list(caseId?: string): Promise<{ packages: DocumentPackage[]; count: number }> {
    const url = caseId ? `${API_URL}/packages?caseId=${caseId}` : `${API_URL}/packages`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to list packages');
    return data.data;
  },

  async get(packageId: string): Promise<{ package: DocumentPackage; documents: DocumentV2[] }> {
    const res = await fetch(`${API_URL}/packages/${packageId}`, {
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to get package');
    return data.data;
  },

  async update(packageId: string, request: UpdatePackageRequest): Promise<DocumentPackage> {
    const res = await fetch(`${API_URL}/packages/${packageId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update package');
    return data.data;
  },

  async delete(packageId: string): Promise<void> {
    const res = await fetch(`${API_URL}/packages/${packageId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to delete package');
  },

  async addDocument(packageId: string, documentId: string): Promise<void> {
    const res = await fetch(`${API_URL}/packages/${packageId}/documents`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ documentId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to add document to package');
  },

  async removeDocument(packageId: string, documentId: string): Promise<void> {
    const res = await fetch(`${API_URL}/packages/${packageId}/documents/${documentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to remove document from package');
  },
};

// ========================================
// Categories API
// ========================================

export const categoriesApi = {
  async list(): Promise<{ categories: Array<{ id: string; name: string; isCustom: boolean }> }> {
    const res = await fetch(`${API_URL}/categories`, {
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to list categories');
    return data.data;
  },

  async create(name: string): Promise<CustomCategory> {
    const res = await fetch(`${API_URL}/categories`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create category');
    return data.data;
  },

  async delete(categoryId: string): Promise<void> {
    const res = await fetch(`${API_URL}/categories/${categoryId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to delete category');
  },
};
